import logger from '@/lib/logger';
import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, IconButton, Menu, MenuItem, Alert } from '@mui/material';
import { MoreVert as MoreVertIcon, RestartAlt as RetriggerIcon } from '@mui/icons-material';
import { ResultCard } from './ResultCard';
import { testService } from '@/services/api/testService';
import { apiClient } from '@/lib/api';
interface ResultsDisplayProps {
  measurementResults: any;
  notes?: string;
  testId?: string;
}

interface ChannelResult {
  channelId: number;
  pathogen: string;
  value: number | string;
  status: 'Negative' | 'Positive' | 'Analyzing' | 'Inconclusive' | 'Retrying' | 'Error';
  interpretation: string;
}

/**
 * Convert measurement results from store to display format
 */
function parseResultsForDisplay(measurementResults: any): ChannelResult[] {
  if (!measurementResults?.channels) return [];
  
  return measurementResults.channels.map((ch: any) => {
    const resultLower = ch.result?.toLowerCase();
    
    if (resultLower === 'analyzing') {
      return {
        channelId: ch.channel,
        pathogen: ch.pathogen,
        value: 'Analyzing...',
        status: 'Analyzing' as const,
        interpretation: 'Classification in progress. Results will appear shortly.',
      };
    }

    if (resultLower === 'retrying') {
      return {
        channelId: ch.channel,
        pathogen: ch.pathogen,
        value: 'Retrying...',
        status: 'Retrying' as const,
        interpretation: 'Classification attempt failed. Retrying automatically...',
      };
    }

    if (resultLower === 'error') {
      return {
        channelId: ch.channel,
        pathogen: ch.pathogen,
        value: 'Failed',
        status: 'Error' as const,
        interpretation: 'Classification failed after all retry attempts. You can re-trigger classification manually.',
      };
    }
    
    if (resultLower === 'positive') {
      return {
        channelId: ch.channel,
        pathogen: ch.pathogen,
        value: 'Positive',
        status: 'Positive' as const,
        interpretation: 'A positive result indicates the target substance was detected.',
      };
    }
    
    if (resultLower === 'inconclusive' || resultLower === 'invalid') {
      return {
        channelId: ch.channel,
        pathogen: ch.pathogen,
        value: 'Inconclusive',
        status: 'Inconclusive' as const,
        interpretation: 'The result is inconclusive. Consider retesting for confirmation.',
      };
    }

    if (resultLower === 'negative') {
      return {
        channelId: ch.channel,
        pathogen: ch.pathogen,
        value: 'Negative',
        status: 'Negative' as const,
        interpretation: 'A negative result indicates the target substance was not detected.',
      };
    }

    // Unknown status — treat as inconclusive rather than silently showing Negative
    return {
      channelId: ch.channel,
      pathogen: ch.pathogen,
      value: 'Inconclusive',
      status: 'Inconclusive' as const,
      interpretation: 'The result is inconclusive. Consider retesting for confirmation.',
    };
  });
}

export function ResultsDisplay({ measurementResults, notes, testId }: ResultsDisplayProps) {
  const [note, setNote] = useState(notes || '');
  const [isNoteSaved, setIsNoteSaved] = useState(!!notes);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRetriggering, setIsRetriggering] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Get results from props (passed by parent)
  const channelResults = parseResultsForDisplay(measurementResults);
  const isRetrying = measurementResults?.retryAttempt != null && !measurementResults?.classificationFailed;
  const isFailed = measurementResults?.classificationFailed === true;
  const classificationError = measurementResults?.classificationError;

  const handleRetriggerClassification = async () => {
    if (!testId) return;
    setIsRetriggering(true);
    try {
      await apiClient.post(`/api/v1/tests/${testId}/classification/retrigger`);
      logger.debug('[ResultsDisplay] Classification re-triggered for test:', testId);
    } catch (error) {
      logger.error('[ResultsDisplay] Failed to re-trigger classification:', error);
    } finally {
      setIsRetriggering(false);
    }
  };

  // Update note when props change
  useEffect(() => {
    if (notes !== undefined) {
      setNote(notes || '');
      setIsNoteSaved(!!notes);
    }
  }, [notes]);

  const handleSaveNote = async () => {
    if (!testId) {
      setIsNoteSaved(true);
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await testService.updateTestNotes(testId, note);
      setIsNoteSaved(true);
      setIsEditing(false);
    } catch (error) {
      logger.error('Failed to save note:', error);
      // TODO: Show error toast
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditNote = () => {
    setIsEditing(true);
    setMenuAnchor(null);
  };

  const handleDeleteNote = async () => {
    if (!testId) {
      setNote('');
      setIsNoteSaved(false);
      setMenuAnchor(null);
      return;
    }

    setIsSaving(true);
    try {
      await testService.updateTestNotes(testId, '');
      setNote('');
      setIsNoteSaved(false);
      setMenuAnchor(null);
    } catch (error) {
      logger.error('Failed to delete note:', error);
      // TODO: Show error toast
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800 }}>
      {/* Classification status banners */}
      {isRetrying && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Classification attempt failed (retry {measurementResults.retryAttempt}/{measurementResults.maxRetries}). Retrying automatically...
          {classificationError && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>
              {classificationError}
            </Typography>
          )}
        </Alert>
      )}
      {isFailed && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            testId ? (
              <Button
                color="inherit"
                size="small"
                startIcon={<RetriggerIcon />}
                onClick={handleRetriggerClassification}
                disabled={isRetriggering}
              >
                {isRetriggering ? 'Retrying...' : 'Retry Classification'}
              </Button>
            ) : undefined
          }
        >
          Classification failed after all retry attempts.
          {classificationError && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>
              {classificationError}
            </Typography>
          )}
        </Alert>
      )}

      {/* 4-Channel Result Cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 2,
        mb: 4,
      }}>
        {channelResults.map((channel, index) => (
          <ResultCard
            key={channel.channelId}
            testName={`CH ${index + 1}`}
            pathogen={channel.pathogen}
            value={channel.value}
            status={channel.status}
          />
        ))}
      </Box>

      {/* Additional Note Section */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Additional note</Typography>
          
          {!isNoteSaved && !isEditing && note && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleSaveNote}
              disabled={isSaving}
            >
              Add note
            </Button>
          )}
          
          {isNoteSaved && !isEditing && (
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
          )}
          
          {isEditing && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleSaveNote}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save changes'}
            </Button>
          )}
        </Box>
        
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem onClick={handleEditNote}>Edit note</MenuItem>
          <MenuItem onClick={handleDeleteNote}>Delete note</MenuItem>
        </Menu>
        
        <TextField
          multiline
          rows={4}
          fullWidth
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={isNoteSaved && !isEditing}
          placeholder="Add additional notes about this test..."
          variant="outlined"
        />
      </Box>
    </Box>
  );
}
